import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";

import {
    Button,
    Container,
    Dimmer,
    Divider,
    Form,
    Header,
    Icon,
    Label as SUILabel,
    List,
    Loader,
    Message,
    Segment,
    Step,
    Table,
} from "semantic-ui-react";

import Alerta from "../components/Alertas";
import useObtenerMantencion from "../hooks/useObtenerMantencion";
import { auth, db } from "../firebase/firebaseConfig";
import {
    addDoc,
    collection,
    doc,
    getDocs,
    query,
    updateDoc,
    where,
    writeBatch,
} from "firebase/firestore";

import { UserContext } from "../context/UserContext";
import { Opcion } from "./TipDoc";

const CATEGORIAS = {
    INSTRUMENTOS: "INSTRUMENTOS",
    CHECK: "CHECK",
    MEDICION: "MEDICION",
    SEGURIDAD: "SEGURIDAD",
};

const toMillisSafe = (x) =>
    x?.toMillis ? x.toMillis() : x?.toDate ? x.toDate().getTime() : +new Date(x ?? 0);

const sortByFechaAddAsc = (a, b) => toMillisSafe(a?.fechaadd) - toMillisSafe(b?.fechaadd);

const isEmpty = (v) => String(v ?? "").trim() === "";

const EjecutarMantencion = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const user = auth.currentUser;
    const { users } = useContext(UserContext);

    const [manto] = useObtenerMantencion(id);

    // UI
    const [step, setStep] = useState(0); // 0=info, 1=mantención
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [alerta, cambiarAlerta] = useState({ tipo: "", mensaje: "" });
    const [estadoAlerta, cambiarEstadoAlerta] = useState(false);

    // Datos mantención/protocolo
    const [nombreProtocolo, setNombreProtocolo] = useState("");
    const [dias, setDias] = useState("");
    const [familia, setFamilia] = useState("");
    const [tipo, setTipo] = useState("");
    const [serie, setSerie] = useState("");
    const [eq_id, setEq_id] = useState("");

    // Items
    const [itemsInst, setItemsInst] = useState([]);
    const [itemsCheck, setItemsCheck] = useState([]);
    const [itemsMedicion, setItemsMedicion] = useState([]);
    const [itemsSeg, setItemsSeg] = useState([]);

    // Estado cabecera bitácora actual
    const protocoloCabRef = useRef("");
    const bitacoraCabIdRef = useRef(""); // cabecera bitácora (bitacoracab)

    // Botones
    const [btnGuardarDisabled, setBtnGuardarDisabled] = useState(false);
    const [btnConfirmarDisabled, setBtnConfirmarDisabled] = useState(false);

    const volver = useCallback(() => {
        navigate("/serviciotecnico/mantencion");
    }, [navigate]);

    // -------------------------
    // Cargar datos base desde manto
    // -------------------------
  /*   useEffect(() => {
        if (!manto) return;

        protocoloCabRef.current = manto.cab_id_protocol || "";
        setNombreProtocolo(manto.nombre_protocolo || "");
        setDias(manto.dias || "");
        setFamilia(manto.familia || "");
        setTipo(manto.tipo || "");
        setSerie(manto.serie || "");
        setEq_id(manto.id_eq || "");

        console.log("manto raw:", manto);
        console.log("cab_id_protocol:", manto?.cab_id_protocol);
        console.log("keys:", manto ? Object.keys(manto) : null);

        if (!manto.cab_id_protocol) {
            Swal.fire("Error", "La mantención no tiene protocolo asociado.", "error").then(volver);
            return;
        }

        setLoading(false);
    }, [manto, volver]); */

    useEffect(() => {
        // 1) si todavía viene null/undefined, no hagas nada
        if (!manto) return;

        // 2) si viene array (tu caso), espera a que tenga contenido
        if (Array.isArray(manto)) {
            if (manto.length === 0) return;     // ⬅️ CLAVE: no validar aún
        }

        // 3) normaliza (si es array toma el primer elemento)
        const m = Array.isArray(manto) ? manto[0] : manto;

        // 4) valida recién cuando ya hay objeto real
        const cabId = m?.cab_id_protocol;
        if (cabId === undefined || cabId === null || cabId === "") {
            Swal.fire("Error", "La mantención no tiene protocolo asociado.", "error").then(() => {
                navigate("/serviciotecnico/mantencion");
            });
            return;
        }

        // 5) carga estados
        protocoloCabRef.current = cabId;
        setNombreProtocolo(m.nombre_protocolo || "");
        setDias(m.dias || "");
        setFamilia(m.familia || "");
        setTipo(m.tipo || "");
        setSerie(m.serie || "");
        setEq_id(m.id_eq || "");

    }, [manto, navigate]);



    // Si manto no existe o id inválido
    useEffect(() => {
        if (manto === undefined) return; // hook aún cargando
        if (!manto) {
            volver();
        }
    }, [manto, volver]);

    const fetchDocs = useCallback(async (q) => {
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
    }, []);

    // -------------------------
    // Cargar plantilla desde "protocolos" (primera vez)
    // -------------------------
    const consultarProtocolos = useCallback(async () => {
        if (!users?.emp_id || !protocoloCabRef.current) return;

        const base = (categoria) =>
            query(
                collection(db, "protocolos"),
                where("emp_id", "==", users.emp_id),
                where("cab_id", "==", protocoloCabRef.current),
                where("categoria", "==", categoria)
            );

        const [inst, chk, med, seg] = await Promise.all([
            fetchDocs(base(CATEGORIAS.INSTRUMENTOS)),
            fetchDocs(base(CATEGORIAS.CHECK)),
            fetchDocs(base(CATEGORIAS.MEDICION)),
            fetchDocs(base(CATEGORIAS.SEGURIDAD)),
        ]);

        setItemsInst(inst.sort(sortByFechaAddAsc));

        // importante: valor vacío
        setItemsCheck(chk.map((x, i) => ({ ...x, id2: i + 1, valor: "" })));
        setItemsMedicion(med.map((x, i) => ({ ...x, id2: i + 1, valor: "" })));
        setItemsSeg(seg.map((x, i) => ({ ...x, id2: i + 1, valor: "" })));
    }, [users?.emp_id, fetchDocs]);

    // -------------------------
    // Buscar cabecera de bitácora pendiente o crear
    // -------------------------
    const getCabeceraBitacoraPendiente = useCallback(async () => {
        if (!users?.emp_id || !serie) return null;

        const qCab = query(
            collection(db, "bitacoracab"),
            where("emp_id", "==", users.emp_id),
            where("serie", "==", serie),
            where("confirmado", "==", false)
        );

        const cabs = await fetchDocs(qCab);
        if (cabs.length === 0) return null;

        const sorted = [...cabs].sort((a, b) => toMillisSafe(b?.fechamod) - toMillisSafe(a?.fechamod));
        bitacoraCabIdRef.current = sorted[0].id;
        return sorted[0].id;
    }, [users?.emp_id, serie, fetchDocs]);

    const createCabeceraBitacora = useCallback(async () => {
        if (!users?.emp_id) throw new Error("No hay emp_id.");
        if (!protocoloCabRef.current) throw new Error("No hay cab_id_protocol.");
        if (!id) throw new Error("No hay id mantención.");

        const now = new Date();

        const docRef = await addDoc(collection(db, "bitacoracab"), {
            nombre_protocolo: nombreProtocolo,
            cab_id_protocolo: protocoloCabRef.current,
            fecha_mantencion: now,
            familia,
            tipo,
            serie,
            eq_id,
            id_manto: id,
            confirmado: false,
            useradd: user?.email ?? "",
            usermod: user?.email ?? "",
            fechaadd: now,
            fechamod: now,
            emp_id: users.emp_id,
        });

        bitacoraCabIdRef.current = docRef.id;
        return docRef.id;
    }, [users?.emp_id, id, nombreProtocolo, familia, tipo, serie, eq_id, user?.email]);

    // -------------------------
    // Cargar detalle desde "bitacoras" (si ya existe avance)
    // -------------------------
    const consultarBitacoras = useCallback(async (cabId) => {
        if (!users?.emp_id || !cabId) return;

        const base = (categoria) =>
            query(
                collection(db, "bitacoras"),
                where("emp_id", "==", users.emp_id),
                where("cab_id_bitacora", "==", cabId),
                where("categoria", "==", categoria)
            );

        const [chk, med, seg] = await Promise.all([
            fetchDocs(base(CATEGORIAS.CHECK)),
            fetchDocs(base(CATEGORIAS.MEDICION)),
            fetchDocs(base(CATEGORIAS.SEGURIDAD)),
        ]);

        setItemsCheck(chk.map((x, i) => ({ ...x, id2: i + 1 })).sort(sortByFechaAddAsc));
        setItemsMedicion(med.map((x, i) => ({ ...x, id2: i + 1 })).sort(sortByFechaAddAsc));
        setItemsSeg(seg.map((x, i) => ({ ...x, id2: i + 1 })).sort(sortByFechaAddAsc));
    }, [users?.emp_id, fetchDocs]);

    // -------------------------
    // Setters por ID (no por índice)
    // -------------------------
    const setCheckValorById = useCallback((itemId, valor) => {
        setItemsCheck((prev) => prev.map((it) => (it.id === itemId ? { ...it, valor } : it)));
    }, []);

    const setMedicionValorById = useCallback((itemId, valor) => {
        setItemsMedicion((prev) => prev.map((it) => (it.id === itemId ? { ...it, valor } : it)));
    }, []);

    const setSeguridadValorById = useCallback((itemId, valor) => {
        setItemsSeg((prev) => prev.map((it) => (it.id === itemId ? { ...it, valor } : it)));
    }, []);

    // -------------------------
    // Helpers
    // -------------------------
    const sumarDias = useCallback((fechaMantencion, diasMant) => {
        // fechaMantencion puede venir como Timestamp (de Firestore)
        const baseDate = fechaMantencion?.toDate ? fechaMantencion.toDate() : new Date(fechaMantencion);
        const m = moment(baseDate).add(Number(diasMant || 0), "days");
        return new Date(m.toDate());
    }, []);

    const isPasaMedicion = useCallback((it) => {
        const v = Number(it.valor);
        const ini = Number(it.inicial);
        const fin = Number(it.final);
        if (!Number.isFinite(v) || isEmpty(it.valor)) return null;
        return v >= ini && v <= fin;
    }, []);

    // -------------------------
    // Inicial: si está en proceso carga bitácoras, si no, carga plantilla
    // -------------------------
    useEffect(() => {
        const run = async () => {
            if (!users?.emp_id || !manto) return;
            if (!serie || !protocoloCabRef.current) return;

            setLoading(true);
            try {
                // si está en proceso => buscar cabecera y cargar bitácoras
                if (String(manto.enproceso) === "1") {
                    const cabId = await getCabeceraBitacoraPendiente();
                    if (cabId) {
                        setStep(1);
                        await consultarBitacoras(cabId);
                    } else {
                        // si enproceso=1 pero no hay cabecera, caemos a plantilla (failsafe)
                        await consultarProtocolos();
                        setStep(0);
                    }
                } else {
                    await consultarProtocolos();
                    setStep(0);
                }
            } catch (e) {
                console.error(e);
                Swal.fire("Error", "No se pudieron cargar los datos.", "error");
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [users?.emp_id, manto, serie, getCabeceraBitacoraPendiente, consultarBitacoras, consultarProtocolos]);

    // -------------------------
    // Siguiente: crea/recupera cabecera y habilita formulario
    // -------------------------
    const onSiguiente = useCallback(async () => {
        if (!users?.emp_id) return;
        setSaving(true);
        cambiarEstadoAlerta(false);
        cambiarAlerta({ tipo: "", mensaje: "" });

        try {
            let cabId = await getCabeceraBitacoraPendiente();
            if (!cabId) cabId = await createCabeceraBitacora();
            if (!cabId) return;

            // si veníamos con plantilla, dejamos paso 1 y seguimos con esa plantilla (valores vacíos)
            setStep(1);

            Swal.fire({
                icon: "success",
                title: "Proceda a realizar la mantención",
                timer: 900,
                showConfirmButton: false,
            });
        } catch (e) {
            console.error(e);
            Swal.fire("Error", String(e), "error");
        } finally {
            setSaving(false);
        }
    }, [users?.emp_id, getCabeceraBitacoraPendiente, createCabeceraBitacora]);

    // -------------------------
    // Guardar avance (crear vs update)
    // -------------------------
    const onGuardar = useCallback(async () => {
        if (!users?.emp_id) return;
        if (!bitacoraCabIdRef.current) {
            Swal.fire("Falta cabecera", "Presiona Siguiente antes de guardar.", "warning");
            return;
        }

        setSaving(true);
        cambiarEstadoAlerta(false);
        cambiarAlerta({ tipo: "", mensaje: "" });

        try {
            const now = new Date();
            const cabId = bitacoraCabIdRef.current;

            // ¿hay docs ya? miramos CHECK
            const qExist = query(
                collection(db, "bitacoras"),
                where("emp_id", "==", users.emp_id),
                where("cab_id_bitacora", "==", cabId),
                where("categoria", "==", CATEGORIAS.CHECK)
            );
            const existe = await fetchDocs(qExist);
            const isFirstSave = existe.length === 0;

            const batch = writeBatch(db);
            const bitRef = collection(db, "bitacoras");

            if (isFirstSave) {
                // crea CHECK
                itemsCheck.forEach((d) => {
                    const ref = doc(bitRef);
                    batch.set(ref, {
                        item: d.item,
                        valor: d.valor || "",
                        categoria: CATEGORIAS.CHECK,
                        cab_id_bitacora: cabId,
                        useradd: user?.email ?? "",
                        usermod: user?.email ?? "",
                        fechaadd: now,
                        fechamod: now,
                        emp_id: users.emp_id,
                    });
                });

                // crea MEDICION
                itemsMedicion.forEach((d) => {
                    const ref = doc(bitRef);
                    batch.set(ref, {
                        item: d.item,
                        valor: d.valor || "",
                        medida: d.medida,
                        inicial: d.inicial,
                        final: d.final,
                        categoria: CATEGORIAS.MEDICION,
                        cab_id_bitacora: cabId,
                        useradd: user?.email ?? "",
                        usermod: user?.email ?? "",
                        fechaadd: now,
                        fechamod: now,
                        emp_id: users.emp_id,
                    });
                });

                // crea SEGURIDAD
                itemsSeg.forEach((d) => {
                    const ref = doc(bitRef);
                    batch.set(ref, {
                        item: d.item,
                        valor: d.valor || "",
                        categoria: CATEGORIAS.SEGURIDAD,
                        cab_id_bitacora: cabId,
                        useradd: user?.email ?? "",
                        usermod: user?.email ?? "",
                        fechaadd: now,
                        fechamod: now,
                        emp_id: users.emp_id,
                    });
                });
            } else {
                // update (deben venir con id)
                itemsCheck.forEach((d) => {
                    if (!d.id) return;
                    batch.update(doc(db, "bitacoras", d.id), {
                        valor: d.valor || "",
                        usermod: user?.email ?? "",
                        fechamod: now,
                    });
                });

                itemsMedicion.forEach((d) => {
                    if (!d.id) return;
                    batch.update(doc(db, "bitacoras", d.id), {
                        valor: d.valor || "",
                        usermod: user?.email ?? "",
                        fechamod: now,
                    });
                });

                itemsSeg.forEach((d) => {
                    if (!d.id) return;
                    batch.update(doc(db, "bitacoras", d.id), {
                        valor: d.valor || "",
                        usermod: user?.email ?? "",
                        fechamod: now,
                    });
                });
            }

            await batch.commit();

            // marca enproceso=1
            await updateDoc(doc(db, "mantenciones", id), {
                enproceso: "1",
                usermod: user?.email ?? "",
                fechamod: now,
            });

            // si fue primer guardado => recarga bitácoras para obtener IDs y ya poder actualizar después
            if (isFirstSave) {
                await consultarBitacoras(cabId);
            }

            cambiarEstadoAlerta(true);
            cambiarAlerta({ tipo: "exito", mensaje: isFirstSave ? "Documento creado correctamente." : "Documento actualizado correctamente." });

            // bloquear botones como tú hacías
            setBtnGuardarDisabled(true);
            setBtnConfirmarDisabled(false);
        } catch (e) {
            console.error(e);
            Swal.fire("Error", "No se pudo guardar.", "error");
        } finally {
            setSaving(false);
        }
    }, [users?.emp_id, itemsCheck, itemsMedicion, itemsSeg, id, user?.email, fetchDocs, consultarBitacoras]);

    // -------------------------
    // Confirmar (validación completa + cierre)
    // -------------------------
    const onConfirmar = useCallback(async () => {
        cambiarEstadoAlerta(false);
        cambiarAlerta({ tipo: "", mensaje: "" });

        if (!users?.emp_id) return;
        if (!bitacoraCabIdRef.current) {
            Swal.fire("Falta cabecera", "Presiona Siguiente antes de confirmar.", "warning");
            return;
        }

        // validación
        const faltanCheck = itemsCheck.some((x) => isEmpty(x.valor));
        const faltanMed = itemsMedicion.some((x) => isEmpty(x.valor));
        const faltanSeg = itemsSeg.some((x) => isEmpty(x.valor));

        if (faltanCheck || faltanMed || faltanSeg) {
            Swal.fire(
                "Faltan datos",
                "No puedes confirmar: hay ítems sin completar. Recuerda guardar antes de confirmar.",
                "warning"
            );
            setBtnGuardarDisabled(false);
            setBtnConfirmarDisabled(true);
            return;
        }

        setSaving(true);
        try {
            // necesitamos cabecera bitacoracab pendiente
            const qCab = query(
                collection(db, "bitacoracab"),
                where("emp_id", "==", users.emp_id),
                where("serie", "==", serie),
                where("confirmado", "==", false)
            );
            const cabs = await fetchDocs(qCab);
            if (cabs.length === 0) {
                Swal.fire("Error", "No se encontró cabecera de bitácora pendiente.", "error");
                return;
            }

            const cab = [...cabs].sort((a, b) => toMillisSafe(b?.fechamod) - toMillisSafe(a?.fechamod))[0];

            const now = new Date();

            // actualizar mantención
            await updateDoc(doc(db, "mantenciones", id), {
                enproceso: "0",
                fecha_inicio: cab.fecha_mantencion ?? now,
                fecha_termino: sumarDias(cab.fecha_mantencion ?? now, dias),
                usermod: user?.email ?? "",
                fechamod: now,
            });

            // confirmar bitácora cab
            await updateDoc(doc(db, "bitacoracab", cab.id), {
                confirmado: true,
                usermod: user?.email ?? "",
                fechamod: now,
            });

            Swal.fire("Check List de Mantención realizado con éxito!", "", "success").then(volver);
        } catch (e) {
            console.error(e);
            Swal.fire("Error", "No se pudo confirmar.", "error");
        } finally {
            setSaving(false);
        }
    }, [users?.emp_id, itemsCheck, itemsMedicion, itemsSeg, serie, id, dias, sumarDias, user?.email, fetchDocs, volver]);

    // -------------------------
    // Derivados UI
    // -------------------------
    const canFinalizar = useMemo(() => {
        if (itemsCheck.length === 0 && itemsMedicion.length === 0 && itemsSeg.length === 0) return false;
        return (
            !itemsCheck.some((x) => isEmpty(x.valor)) &&
            !itemsMedicion.some((x) => isEmpty(x.valor)) &&
            !itemsSeg.some((x) => isEmpty(x.valor))
        );
    }, [itemsCheck, itemsMedicion, itemsSeg]);

    const instrumentosSorted = useMemo(() => [...itemsInst].sort(sortByFechaAddAsc), [itemsInst]);

    // -------------------------
    // Render
    // -------------------------
    return (
        <Container fluid style={{ maxWidth: 980, paddingBottom: 24 }}>
            <Segment inverted color="teal" tertiary>
                <Header as="h2" icon textAlign="center">
                    <Icon name="wrench" />
                    Ejecutar Mantención
                    <Header.Subheader>{nombreProtocolo || "—"}</Header.Subheader>
                </Header>
            </Segment>

            <Dimmer active={loading || saving} inverted>
                <Loader>{saving ? "Guardando..." : "Cargando..."}</Loader>
            </Dimmer>

            <Segment raised>
                <Step.Group fluid size="small">
                    <Step active={step === 0} completed={step > 0}>
                        <Icon name="info circle" />
                        <Step.Content>
                            <Step.Title>Información</Step.Title>
                            <Step.Description>Protocolo y equipo</Step.Description>
                        </Step.Content>
                    </Step>
                    <Step active={step === 1} disabled={step < 1}>
                        <Icon name="tasks" />
                        <Step.Content>
                            <Step.Title>Mantención</Step.Title>
                            <Step.Description>Checklist / Medición / Seguridad</Step.Description>
                        </Step.Content>
                    </Step>
                </Step.Group>
            </Segment>

            <Segment raised>
                <Header as="h3" dividing>
                    <Icon name="clipboard list" />
                    Información
                </Header>

                <Table celled compact size="small">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Familia</Table.HeaderCell>
                            <Table.HeaderCell>Tipo</Table.HeaderCell>
                            <Table.HeaderCell>Serie</Table.HeaderCell>
                            <Table.HeaderCell>Días</Table.HeaderCell>
                            <Table.HeaderCell>Equipo ID</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell>{familia}</Table.Cell>
                            <Table.Cell>{tipo}</Table.Cell>
                            <Table.Cell>{serie}</Table.Cell>
                            <Table.Cell>{dias}</Table.Cell>
                            <Table.Cell>{eq_id}</Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>

                {instrumentosSorted.length > 0 && (
                    <Segment secondary>
                        <Header as="h4">
                            <Icon name="list ul" />
                            Instrumentos requeridos
                        </Header>
                        <List bulleted>
                            {instrumentosSorted.map((it) => (
                                <List.Item key={it.id} style={{ fontSize: 13 }}>
                                    {it.item}
                                </List.Item>
                            ))}
                        </List>
                    </Segment>
                )}

                <Divider />

                {step === 0 && (
                    <Button color="teal" icon labelPosition="right" onClick={onSiguiente} disabled={saving || loading}>
                        Siguiente
                        <Icon name="arrow right" />
                    </Button>
                )}

                {step === 1 && (
                    <>
                        {/* CHECK */}
                        {itemsCheck.length > 0 && (
                            <Segment>
                                <Header as="h3">
                                    <Icon name="check square" />
                                    Checklist
                                </Header>

                                <Table celled compact>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell width={1}>N°</Table.HeaderCell>
                                            <Table.HeaderCell>Item</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="center" width={6}>
                                                Resultado
                                            </Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {itemsCheck.map((it, idx) => (
                                            <Table.Row key={it.id}>
                                                <Table.Cell>{idx + 1}</Table.Cell>
                                                <Table.Cell style={{ whiteSpace: "normal" }}>{it.item}</Table.Cell>
                                                <Table.Cell textAlign="center">
                                                    <Button.Group size="tiny">
                                                        <Button
                                                            positive={it.valor === "pasa"}
                                                            onClick={() => setCheckValorById(it.id, "pasa")}
                                                        >
                                                            Pasa
                                                        </Button>
                                                        <Button
                                                            negative={it.valor === "nopasa"}
                                                            onClick={() => setCheckValorById(it.id, "nopasa")}
                                                        >
                                                            No pasa
                                                        </Button>
                                                        <Button
                                                            basic
                                                            color={it.valor === "na" ? "blue" : undefined}
                                                            onClick={() => setCheckValorById(it.id, "na")}
                                                        >
                                                            N/A
                                                        </Button>
                                                    </Button.Group>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </Segment>
                        )}

                        {/* MEDICION */}
                        {itemsMedicion.length > 0 && (
                            <Segment>
                                <Header as="h3">
                                    <Icon name="tachometer alternate" />
                                    Medición
                                </Header>

                                <Table celled compact>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell width={1}>N°</Table.HeaderCell>
                                            <Table.HeaderCell>Item</Table.HeaderCell>
                                            <Table.HeaderCell width={3}>Valor</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="center" width={3}>
                                                Rango
                                            </Table.HeaderCell>
                                            <Table.HeaderCell width={2}>Unidad</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="center" width={1}>
                                                Pasa
                                            </Table.HeaderCell>
                                            <Table.HeaderCell textAlign="center" width={1}>
                                                No pasa
                                            </Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>

                                    <Table.Body>
                                        {itemsMedicion.map((it, idx) => {
                                            const pasa = isPasaMedicion(it);
                                            return (
                                                <Table.Row key={it.id}>
                                                    <Table.Cell>{idx + 1}</Table.Cell>
                                                    <Table.Cell style={{ whiteSpace: "normal" }}>{it.item}</Table.Cell>
                                                    <Table.Cell>
                                                        <Form.Input
                                                            type="number"
                                                            value={it.valor ?? ""}
                                                            onChange={(e, d) => setMedicionValorById(it.id, d.value)}
                                                            size="small"
                                                        />
                                                    </Table.Cell>
                                                    <Table.Cell textAlign="center">
                                                        {it.inicial} - {it.final}
                                                    </Table.Cell>
                                                    <Table.Cell>{it.medida}</Table.Cell>
                                                    <Table.Cell textAlign="center">
                                                        {pasa === true && <Icon name="check" color="green" />}
                                                        {pasa === null && <Icon name="minus" color="grey" />}
                                                    </Table.Cell>
                                                    <Table.Cell textAlign="center">
                                                        {pasa === false && <Icon name="close" color="red" />}
                                                        {pasa === null && <Icon name="minus" color="grey" />}
                                                    </Table.Cell>
                                                </Table.Row>
                                            );
                                        })}
                                    </Table.Body>
                                </Table>
                            </Segment>
                        )}

                        {/* SEGURIDAD */}
                        {itemsSeg.length > 0 && (
                            <Segment>
                                <Header as="h3">
                                    <Icon name="shield alternate" />
                                    Seguridad eléctrica
                                </Header>

                                <Table celled compact>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell width={1}>N°</Table.HeaderCell>
                                            <Table.HeaderCell>Item</Table.HeaderCell>
                                            <Table.HeaderCell width={6}>Valor</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {itemsSeg.map((it, idx) => (
                                            <Table.Row key={it.id}>
                                                <Table.Cell>{idx + 1}</Table.Cell>
                                                <Table.Cell style={{ whiteSpace: "normal" }}>
                                                    {it.item === "CLASIFICACION" ? <SUILabel color="blue">CLASIFICACIÓN</SUILabel> : it.item}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {it.item === "CLASIFICACION" ? (
                                                        <Form.Select
                                                            placeholder="Seleccione opción"
                                                            options={Opcion.map((o, i) => ({ key: i, text: o.text, value: o.text }))}
                                                            value={it.valor ?? ""}
                                                            onChange={(e, d) => setSeguridadValorById(it.id, d.value)}
                                                        />
                                                    ) : (
                                                        <Form.Input
                                                            value={it.valor ?? ""}
                                                            onChange={(e, d) => setSeguridadValorById(it.id, d.value)}
                                                            placeholder="Ingrese valor"
                                                        />
                                                    )}
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </Segment>
                        )}

                        <Segment>
                            <Message info icon>
                                <Icon name="info circle" />
                                <Message.Content>
                                    <Message.Header>Flujo recomendado</Message.Header>
                                    Guarda avance cuando quieras. Para confirmar, asegúrate que todos los ítems estén completos.
                                </Message.Content>
                            </Message>

                            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                                <Button
                                    color="teal"
                                    icon
                                    labelPosition="left"
                                    onClick={onGuardar}
                                    disabled={saving || loading || btnGuardarDisabled}
                                >
                                    <Icon name="save" />
                                    Guardar
                                </Button>

                                <Button
                                    color="green"
                                    icon
                                    labelPosition="left"
                                    onClick={onConfirmar}
                                    disabled={saving || loading || btnConfirmarDisabled || !canFinalizar}
                                    title={!canFinalizar ? "Completa todos los campos antes de confirmar" : "Confirmar"}
                                >
                                    <Icon name="check" />
                                    Confirmar
                                </Button>
                            </div>
                        </Segment>
                    </>
                )}
            </Segment>

            <Segment basic>
                <Button fluid basic color="grey" onClick={volver} disabled={saving || loading}>
                    <Icon name="arrow left" /> Volver
                </Button>
            </Segment>

            <Alerta
                tipo={alerta.tipo}
                mensaje={alerta.mensaje}
                estadoAlerta={estadoAlerta}
                cambiarEstadoAlerta={cambiarEstadoAlerta}
            />
        </Container>
    );
};

export default EjecutarMantencion;